import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EntityHeader, TabBar, SectionCard, Button, DataTable, Modal, TextInput, Select, Textarea,
  LinkCell, useToast, formatDate, type Column,
} from '@datahub/design-system'
import { get, post, patch, downloadCsv } from '../../lib/api'
import { StatusTag } from '../../components/StatusTag'
import type { AdminUser } from '../../lib/types'
import { tabsForEntity, type EntityType } from '@datahub/shared'
import { EntityProfilePanel, PreloadedRowsPanel } from './CompanyProfilePanel'
import { StearsCoreCard, type CoreProfileSnapshot } from './StearsCoreCard'
import { StearsCompanyPicker } from './StearsCompanyPicker'
import { StearsFundPicker } from './StearsFundPicker'
import { StearsFundCard, type CoreFundProfileSnapshot } from './StearsFundCard'
import { StearsPortfolioCard, normalisePortfolio } from './StearsPortfolioCard'
import { StearsTransactionsCard } from './StearsTransactionsCard'
import { AccessLinkLine, type LinkResponse } from './AccessLinkLine'
import { recordRows, countryNamesShown } from './record-rows'

const TABS = ['Overview', 'Contacts', 'Cycles and submissions', 'Emails', 'Activity']

/** What the page calls the entity, by kind. */
const TYPE_LABEL: Record<EntityType, string> = {
  company: 'Datahub company',
  gp: 'Fund manager (GP)',
  lp: 'Limited partner (LP)',
}

interface CompanyDetailData {
  company: {
    id: string; displayName: string; legalCompanyName: string; status: string; timezone: string
    entityType: EntityType
    sector: string | null; countryOfIncorporation: string | null; websiteUrl: string | null
    employeeCount: string | null; typeIfBanking: string | null; ownershipStatus: string | null
    subSectors: string[] | null
    investorType: string[]; investorStatus: string | null; legalStructure: string | null; ticketType: string | null
    hqCityCountry: string | null; hqCountry: string | null; hqRegion: string | null; baseCurrency: string | null
    countriesOfOperation: string[]; ticketSizeCurrency: string | null
    assetsUnderManagement: string | number | null; totalCommitted: string | number | null
    regionsOfInvestment: string[]; sectorPreferences: string[]; investmentStages: string[]; ticketSizes: string[]
    coreSlug: string | null; coreProfile: CoreProfileSnapshot | CoreFundProfileSnapshot | null; coreProfileFetchedAt: string | null
    owner: { id: string; displayName: string | null; email: string } | null
    cadence: { cadence: string; firstPeriodEnd: string; windowDays: number; openOffsetDays: number; pausedAt: string | null } | null
    contacts: { id: string; fullName: string; roleTitle: string | null; email: string; status: string; isPrimary: boolean }[]
    cycles: {
      id: string; periodLabel: string; kind: string; status: string; openedAt: string | null; deadlineAt: string | null
      draft: { overallProgressPct: number; lastSavedAt: string } | null
      submissions: { id: string; version: number; reference: string; submittedAt: string; submittedByName: string; copiedToCoreStatus: string }[]
    }[]
  }
}

/**
 * One entity's page: a company, a fund manager or a limited partner. All three are the
 * same record with the same link, cadence, cycles and submissions, so this is one page
 * with one set of handlers; what changes is the path it talks to, what it calls the
 * entity, and the columns the Overview shows.
 */
export function EntityDetail({ admin, kind }: { admin: AdminUser; kind: 'company' | 'fund' }) {
  const { id } = useParams<{ id: string }>()
  const basePath = kind === 'fund' ? '/admin/funds' : '/admin/companies'
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState(TABS[0]!)
  const [linkModal, setLinkModal] = useState<string | null>(null)
  const [adhocOpen, setAdhocOpen] = useState(false)
  const [nudgeOpen, setNudgeOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [cadenceOpen, setCadenceOpen] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [adhoc, setAdhoc] = useState({ periodLabel: '', windowDays: 14, requestedTabs: [] as string[], customMessage: '' })
  const [nudge, setNudge] = useState('')
  const [contact, setContact] = useState({ fullName: '', roleTitle: '', email: '' })
  const [cadence, setCadence] = useState({ cadence: 'quarterly', firstPeriodEnd: '', openOffsetDays: 1, windowDays: 21 })

  const { data, isLoading } = useQuery({
    queryKey: [kind, id],
    queryFn: () => get<CompanyDetailData>(`${basePath}/${id}`),
    enabled: Boolean(id),
  })
  const company = data?.company

  /**
   * The entity's live reporting link, read once for the page: the header line shows it
   * and the toolbar button uses it to decide whether it is creating or replacing one.
   */
  const link = useQuery({
    queryKey: ['entity-link', id],
    queryFn: () => get<LinkResponse>(`${basePath}/${id}/link`),
    enabled: Boolean(id),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [kind, id] })
    void queryClient.invalidateQueries({ queryKey: ['entity-link', id] })
  }

  const rotate = useMutation({
    mutationFn: () => post<{ url: string }>(`${basePath}/${id}/token/rotate`),
    onSuccess: (res) => { setLinkModal(res.url); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })
  const setStatus = useMutation({
    mutationFn: (status: string) => post(`${basePath}/${id}/status`, { status }),
    onSuccess: () => { toast.success('Company status updated'); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })
  const createAdhoc = useMutation({
    mutationFn: () => post(`${basePath}/${id}/cycles/adhoc`, { ...adhoc, customMessage: adhoc.customMessage || null }),
    onSuccess: () => { toast.success('Ad-hoc request sent'); setAdhocOpen(false); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })
  const sendNudge = useMutation({
    mutationFn: () => post(`${basePath}/${id}/nudge`, { message: nudge || undefined }),
    onSuccess: () => { toast.success('Nudge sent'); setNudgeOpen(false); setNudge('') },
    onError: (e: Error) => toast.error(e.message),
  })
  const addContact = useMutation({
    mutationFn: () => post(`${basePath}/${id}/contacts`, { ...contact, roleTitle: contact.roleTitle || null }),
    onSuccess: () => { toast.success('Contact added'); setContactOpen(false); setContact({ fullName: '', roleTitle: '', email: '' }); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })
  /** CMP-01: refresh this company from Stears Core, or link it to a Core record first. */
  const pullFromStears = useMutation({
    mutationFn: (slug?: string) => post(`${basePath}/${id}/stears/pull`, slug ? { slug } : {}),
    onSuccess: () => {
      toast.success('Pulled the latest from Stears')
      setLinkOpen(false)
      refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const saveCadence = useMutation({
    mutationFn: () => api(`${basePath}/${id}/cadence`, cadence),
    onSuccess: () => { toast.success('Cadence saved'); setCadenceOpen(false); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })
  async function api(path: string, body: unknown) {
    const res = await fetch(`/api/v1${path}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error((await res.json()).error?.message ?? 'That did not save')
    return res.json()
  }

  if (isLoading || !company) {
    return <div className="font-mulish text-text-support" style={{ padding: 20, fontSize: 12 }}>Loading…</div>
  }

  const isFund = company.entityType !== 'company'
  const noun = company.entityType === 'gp' ? 'fund manager' : company.entityType === 'lp' ? 'limited partner' : 'company'

  const cycleColumns: Column<CompanyDetailData['company']['cycles'][number]>[] = [
    { key: 'period', header: 'Period', width: 180, sticky: true, render: (c) => c.periodLabel },
    { key: 'kind', header: 'Type', width: 100, render: (c) => (c.kind === 'adhoc' ? 'Ad-hoc' : c.kind === 'self_serve' ? 'Company' : 'Regular') },
    { key: 'status', header: 'Status', width: 140, render: (c) => <StatusTag status={c.status} /> },
    { key: 'opened', header: 'Opened', width: 130, render: (c) => formatDate(c.openedAt) },
    { key: 'deadline', header: 'Deadline', width: 130, render: (c) => formatDate(c.deadlineAt) },
    { key: 'completion', header: 'Completion', width: 110, align: 'right', render: (c) => `${c.draft?.overallProgressPct ?? 0}%` },
    {
      key: 'submissions', header: 'Submissions', width: 320,
      render: (c) =>
        c.submissions.length === 0 ? '-' : (
          <span className="flex flex-wrap" style={{ gap: 8 }}>
            {c.submissions.map((s) => (
              <LinkCell key={s.id} onClick={() => navigate(`/admin/submissions/${s.id}`)}>
                v{s.version} · {formatDate(s.submittedAt)}
              </LinkCell>
            ))}
          </span>
        ),
    },
  ]

  const contactColumns: Column<CompanyDetailData['company']['contacts'][number]>[] = [
    { key: 'name', header: 'Name', width: 220, sticky: true, render: (c) => c.fullName },
    { key: 'role', header: 'Role', width: 200, render: (c) => c.roleTitle ?? '-' },
    { key: 'email', header: 'Email', width: 280, render: (c) => c.email },
    { key: 'status', header: 'Status', width: 150, render: (c) => <StatusTag status={c.status} /> },
    { key: 'primary', header: 'Primary', width: 100, render: (c) => (c.isPrimary ? 'Yes' : '-') },
  ]

  return (
    <div className="flex flex-col bg-background-body" style={{ padding: 20, height: '100%' }}>
      <EntityHeader
        name={company.displayName}
        type={TYPE_LABEL[company.entityType]}
        subline={<AccessLinkLine link={link.data} isLoading={link.isLoading} />}
      />
      <TabBar
        tabs={TABS}
        active={tab}
        onChange={setTab}
        rightSlot={
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="outline" size="extra-small" onClick={() => rotate.mutate()}>
              {link.data?.hasToken ? 'Rotate link' : 'Create link'}
            </Button>
            <Button
              variant="outline"
              size="extra-small"
              disabled={pullFromStears.isPending}
              onClick={() => (company.coreSlug ? pullFromStears.mutate(undefined) : setLinkOpen(true))}
            >
              {pullFromStears.isPending ? 'Pulling…' : company.coreSlug ? 'Pull from Stears' : 'Link to Stears'}
            </Button>
            <Button variant="outline" size="extra-small" onClick={() => setCadenceOpen(true)}>Cadence</Button>
            <Button variant="outline" size="extra-small" onClick={() => setNudgeOpen(true)}>Nudge</Button>
            <Button variant="outline" size="extra-small" onClick={() => setAdhocOpen(true)}>Ad-hoc request</Button>
            <Button
              variant="outline"
              size="extra-small"
              onClick={() => setStatus.mutate(company.status === 'paused' ? 'active' : company.status === 'active' ? 'paused' : 'active')}
            >
              {company.status === 'paused' ? 'Resume' : company.status === 'active' ? 'Pause' : 'Activate'}
            </Button>
          </div>
        }
      />

      <div
        className="flex-1 overflow-y-auto bg-background-main"
        style={{ padding: 20, borderLeft: '0.5px solid #4B5563', borderRight: '0.5px solid #4B5563', borderBottom: '0.5px solid #4B5563' }}
      >
        {tab === 'Overview' && (
          <div className="flex flex-col" style={{ gap: 20 }}>
            <div className="grid grid-cols-2 items-start gap-5">
              <div className="flex flex-col gap-5">
                <SectionCard title={isFund ? 'Fund data' : 'Corporate data'}>
                  <div className="flex flex-col">
                    {recordRows(company).map(([label, value]) => (
                      <div key={label} className="flex items-center" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                        <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
                        <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
              <div className="flex flex-col gap-5">
                <SectionCard title="Reporting cadence">
                  <div className="flex flex-col">
                    {company.cadence ? (
                      [
                        ['Cadence', company.cadence.cadence],
                        ['First period end', company.cadence.firstPeriodEnd],
                        ['Opens after', `${company.cadence.openOffsetDays} day(s)`],
                        ['Response window', `${company.cadence.windowDays} days`],
                        ['Paused', company.cadence.pausedAt ? 'Yes' : 'No'],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                          <span className="font-mulish text-white" style={{ fontSize: 12, width: '42%' }}>{label}</span>
                          <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{value}</span>
                        </div>
                      ))
                    ) : (
                      <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: '12px 20px' }}>
                        No cadence set. This company will not receive update requests.
                      </p>
                    )}
                  </div>
                </SectionCard>
              </div>
            </div>
            {!company.coreSlug && (
              <SectionCard title="Stears Core">
                <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px' }}>
                  Not linked to a {noun} on Stears. Use “Link to Stears” above to match it and seed this profile with
                  what Stears holds.
                </p>
              </SectionCard>
            )}
            <EntityProfilePanel entityId={company.id} entityType={company.entityType} basePath={basePath} />
            <PreloadedRowsPanel entityId={company.id} entityType={company.entityType} basePath={basePath} />
            <StearsTransactionsCard entityId={company.id} entityType={company.entityType} basePath={basePath} />
            {company.coreProfile && isFund && (
              <StearsPortfolioCard
                companies={normalisePortfolio((company.coreProfile as CoreFundProfileSnapshot).portfolioCompanies)}
              />
            )}
            {company.coreProfile && (isFund
              ? (
                <StearsFundCard
                  profile={company.coreProfile as CoreFundProfileSnapshot}
                  fetchedAt={company.coreProfileFetchedAt}
                  shownCountries={countryNamesShown(company)}
                  ticketSizeCurrency={company.ticketSizeCurrency}
                />
              )
              : (
                <StearsCoreCard
                  profile={company.coreProfile as CoreProfileSnapshot}
                  fetchedAt={company.coreProfileFetchedAt}
                  mappedSubSectors={company.subSectors ?? []}
                />
              ))}
          </div>
        )}

        {tab === 'Contacts' && (
          <div className="flex flex-col" style={{ gap: 20 }}>
            <SectionCard title="Contacts">
              <DataTable columns={contactColumns} rows={company.contacts} rowKey={(c) => c.id} caption="Contacts" emptyMessage="No contacts yet" />
            </SectionCard>
            <div>
              <Button variant="outline" size="small" onClick={() => setContactOpen(true)}>Add contact</Button>
            </div>
          </div>
        )}

        {tab === 'Cycles and submissions' && (
          <SectionCard title="Cycles" exportable>
            <DataTable columns={cycleColumns} rows={company.cycles} rowKey={(c) => c.id} emptyMessage="No cycles yet" />
          </SectionCard>
        )}

        {tab === 'Emails' && <EmailLog companyId={company.id} />}
        {tab === 'Activity' && <ActivityLog companyId={company.id} />}
      </div>

      {/* ------------------------------------------------------------ modals */}
      <Modal open={linkModal !== null} onClose={() => setLinkModal(null)} title="Access link" primaryLabel="Copy link" onPrimary={() => { void navigator.clipboard.writeText(linkModal ?? ''); toast.success('Link copied') }}>
        <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px', marginBottom: 16 }}>
          This is the link from now on, and it also sits under the {noun}'s name on this page. Any previous link stopped
          working the moment this one was issued, so send this one to every contact; they all share it.
        </p>
        <code className="font-mono text-text-hyperlink" style={{ fontSize: 12, wordBreak: 'break-all', display: 'block', padding: 12, background: '#1F2937', border: '0.5px solid #4B5563' }}>
          {linkModal}
        </code>
      </Modal>

      <Modal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Link to Stears"
        primaryLabel="Close"
        onPrimary={() => setLinkOpen(false)}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '18px' }}>
            Find {company.displayName} on Stears. Picking a match pulls its profile straight onto this {noun}.
          </p>
          {isFund ? (
            <StearsFundPicker
              kind={company.entityType === 'lp' ? 'lp' : 'gp'}
              disabled={pullFromStears.isPending}
              onPick={(match) => pullFromStears.mutate(match.slug)}
            />
          ) : (
            <StearsCompanyPicker
              disabled={pullFromStears.isPending}
              onPick={(match) => pullFromStears.mutate(match.slug)}
            />
          )}
        </div>
      </Modal>

      <Modal open={cadenceOpen} onClose={() => setCadenceOpen(false)} title="Reporting cadence" primaryLabel="Save" onPrimary={() => saveCadence.mutate()} primaryLoading={saveCadence.isPending}>
        <div className="flex flex-col" style={{ gap: 20 }}>
          <Select
            label="Cadence" size="large"
            options={[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['semi_annual', 'Semi-annual'], ['annual', 'Annual']].map(([value, label]) => ({ value: value!, label: label! }))}
            value={cadence.cadence} onChange={(v) => setCadence({ ...cadence, cadence: v })}
          />
          <TextInput label="First period end (YYYY-MM-DD)" size="large" value={cadence.firstPeriodEnd} onChange={(v) => setCadence({ ...cadence, firstPeriodEnd: v })} placeholder="2026-12-31" />
          <TextInput label="Open offset in days" size="large" value={String(cadence.openOffsetDays)} onChange={(v) => setCadence({ ...cadence, openOffsetDays: Number(v) || 0 })} />
          <TextInput label="Response window in days" size="large" value={String(cadence.windowDays)} onChange={(v) => setCadence({ ...cadence, windowDays: Number(v) || 14 })} />
        </div>
      </Modal>

      <Modal open={contactOpen} onClose={() => setContactOpen(false)} title="Add contact" primaryLabel="Add" primaryLoading={addContact.isPending} primaryDisabled={!contact.fullName || !contact.email} onPrimary={() => addContact.mutate()}>
        <div className="flex flex-col" style={{ gap: 20 }}>
          <TextInput label="Name" size="large" value={contact.fullName} onChange={(v) => setContact({ ...contact, fullName: v })} />
          <TextInput label="Role" size="large" value={contact.roleTitle} onChange={(v) => setContact({ ...contact, roleTitle: v })} />
          <TextInput label="Email" size="large" inputType="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
        </div>
      </Modal>

      <Modal open={nudgeOpen} onClose={() => setNudgeOpen(false)} title="Send a nudge" primaryLabel="Send" primaryLoading={sendNudge.isPending} onPrimary={() => sendNudge.mutate()}>
        <Textarea label="Message (optional)" value={nudge} onChange={setNudge} rows={4} maxLength={2000} help="Sent to every active contact with the company's link." />
      </Modal>

      <Modal open={adhocOpen} onClose={() => setAdhocOpen(false)} title="Ad-hoc data request" primaryLabel="Open request" primaryDisabled={!adhoc.periodLabel.trim()} primaryLoading={createAdhoc.isPending} onPrimary={() => createAdhoc.mutate()}>
        <div className="flex flex-col" style={{ gap: 20 }}>
          <TextInput label="Label" size="large" value={adhoc.periodLabel} onChange={(v) => setAdhoc({ ...adhoc, periodLabel: v })} placeholder="Series B announced 3 Sep 2026" />
          <TextInput label="Window in days" size="large" value={String(adhoc.windowDays)} onChange={(v) => setAdhoc({ ...adhoc, windowDays: Number(v) || 14 })} />
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span className="font-mulish text-text-support" style={{ fontSize: 12 }}>Tabs to request (leave empty for all)</span>
            <div className="flex flex-wrap" style={{ gap: 12 }}>
              {tabsForEntity(company.entityType).filter((t) => t.key !== 'review').map((t) => (
                <label key={t.key} className="flex items-center font-jakarta text-white" style={{ gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adhoc.requestedTabs.includes(t.key)}
                    onChange={(e) =>
                      setAdhoc({
                        ...adhoc,
                        requestedTabs: e.target.checked ? [...adhoc.requestedTabs, t.key] : adhoc.requestedTabs.filter((k) => k !== t.key),
                      })
                    }
                    style={{ accentColor: '#4B5563' }}
                  />
                  {t.shortLabel}
                </label>
              ))}
            </div>
          </div>
          <Textarea label="Message (optional)" value={adhoc.customMessage} onChange={(v) => setAdhoc({ ...adhoc, customMessage: v })} rows={3} maxLength={2000} />
        </div>
      </Modal>
      <span hidden>{admin.email}</span>
    </div>
  )
}

function EmailLog({ companyId }: { companyId: string }) {
  const { data } = useQuery({
    queryKey: ['company-emails', companyId],
    queryFn: () => get<{ events: { id: string; template: string; subject: string; toEmail: string; status: string; createdAt: string }[] }>(`/admin/companies/${companyId}/emails`),
  })
  const columns: Column<NonNullable<typeof data>['events'][number]>[] = [
    { key: 'sent', header: 'Sent', width: 160, sticky: true, render: (e) => formatDate(e.createdAt) },
    { key: 'template', header: 'Template', width: 200, render: (e) => e.template },
    { key: 'to', header: 'To', width: 260, render: (e) => e.toEmail },
    { key: 'subject', header: 'Subject', width: 380, render: (e) => e.subject },
    { key: 'status', header: 'Status', width: 140, render: (e) => <StatusTag status={e.status === 'sent' ? 'open' : e.status} /> },
  ]
  return (
    <SectionCard title="Emails">
      <DataTable columns={columns} rows={data?.events ?? []} rowKey={(e) => e.id} emptyMessage="No emails sent yet" />
    </SectionCard>
  )
}

function ActivityLog({ companyId }: { companyId: string }) {
  const { data } = useQuery({
    queryKey: ['company-activity', companyId],
    queryFn: () => get<{ entries: { id: string; action: string; actorLabel: string; at: string }[] }>(`/admin/companies/${companyId}/activity`),
  })
  const columns: Column<NonNullable<typeof data>['entries'][number]>[] = [
    { key: 'at', header: 'When', width: 180, sticky: true, render: (e) => formatDate(e.at) },
    { key: 'action', header: 'Action', width: 280, render: (e) => e.action },
    { key: 'actor', header: 'By', width: 240, render: (e) => e.actorLabel },
  ]
  return (
    <SectionCard title="Activity">
      <DataTable columns={columns} rows={data?.entries ?? []} rowKey={(e) => e.id} emptyMessage="Nothing recorded yet" />
    </SectionCard>
  )
}

export { downloadCsv, patch }
